import { PROJECTS } from '../data/portfolio.js';

export function escapeHtml(value) {
  return String(value).replaceAll('&', '&amp;').replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;');
}

/** Links remain usable even when the interactive viewer cannot start. */
export function renderProjectMedia(project) {
  if (!project.media?.length) return '';
  const first = project.media[0];
  const e = escapeHtml;
  return `<div class="project-evidence">
    <a class="project-evidence-cover" href="${e(first.src)}" data-project-media="${e(project.id)}" data-media-index="0" aria-label="Enlarge ${e(project.title)} screenshot">
      <img src="${e(first.src)}" alt="${e(project.title)} — ${e(first.title)}" width="1440" height="960" loading="lazy" decoding="async">
      <span>View screenshot <span aria-hidden="true">↗</span></span>
    </a>
    <p class="project-evidence-caption">${e(first.caption)}</p>
    <div class="project-evidence-actions">${project.media.map((item, index) => `<a href="${e(item.src)}" data-project-media="${e(project.id)}" data-media-index="${index}">${item.type === 'video' ? '<span aria-hidden="true">▶</span> ' : ''}${e(item.title)}</a>${item.type === 'carousel' ? `<noscript>${item.items.map((card, cardIndex) => `<a href="${e(card.src)}">Carousel card ${cardIndex + 1}</a>`).join('')}</noscript>` : ''}`).join('')}</div>
  </div>`;
}

export function renderSelectedProject(project) {
  const e = escapeHtml;
  return `<article class="selected-work-card" id="project-${e(project.id)}" data-world-zone="${e(project.worldZone)}">
    <p class="project-subtitle">${e(project.category)}</p><h3>${e(project.title)}</h3>
    <p>${e(project.summary)}</p>${renderProjectMedia(project)}
    <details><summary>How it works &amp; my contribution</summary>
      <div class="project-field"><strong>My contribution</strong><span>${e(project.proof)}</span></div>
      <ol>${project.steps.map(step => `<li>${e(step)}</li>`).join('')}</ol>
      <p>${e(project.decision)}</p><p class="skill-tech">${e(project.stack.join(' · '))}</p>
    </details>${project.url ? `<a class="project-live-link" href="${e(project.url)}" target="_blank" rel="noopener">Visit ${e(new URL(project.url).hostname)} ↗</a>` : ''}
  </article>`;
}

export function setupProjectMedia(root, { onOpenChange = () => {} } = {}) {
  const dialog = document.createElement('dialog');
  dialog.className = 'project-media-viewer';
  dialog.setAttribute('aria-labelledby', 'project-media-title');
  dialog.setAttribute('aria-describedby', 'project-media-caption');
  dialog.innerHTML = `<header><div><p id="project-media-project"></p><h2 id="project-media-title"></h2></div><button type="button" data-media-close autofocus aria-label="Close project media"><span class="project-media-close-label">Back to portfolio</span><span class="project-media-close-short">Close</span> <span aria-hidden="true">×</span></button></header>
    <div class="project-modal-body">
      <nav class="project-modal-projects" aria-label="All projects"><p>ALL ${PROJECTS.length} PROJECTS</p>${PROJECTS.map(item => `<button type="button" data-gallery-project="${escapeHtml(item.id)}" aria-label="View project: ${escapeHtml(item.title)}"><img src="${escapeHtml(item.media[0].src)}" alt="" width="72" height="48" loading="lazy"><span>${escapeHtml(item.title)}<small>${item.media.length} ${item.media.length === 1 ? 'capture' : 'captures'}</small></span></button>`).join('')}</nav>
      <div class="project-modal-workspace">
        <div class="project-media-stage"></div>
        <nav class="project-media-thumbnails" aria-label="Screenshots and videos"></nav>
        <section class="project-modal-about" aria-label="About this project"></section>
      </div>
    </div>
    <footer><p id="project-media-caption"></p><nav aria-label="Project media"><button type="button" data-media-prev aria-label="Previous media">←</button><span data-media-position aria-live="polite"></span><button type="button" data-media-next aria-label="Next media">→</button></nav></footer>`;
  document.body.append(dialog);
  const stage = dialog.querySelector('.project-media-stage');
  let project, index = 0, trigger;
  function stopMedia() { stage.querySelector('video')?.pause(); }
  function selectProject(selected) {
    project = selected;
    index = 0;
    dialog.querySelectorAll('[data-gallery-project]').forEach(button => {
      button.setAttribute('aria-pressed', String(button.dataset.galleryProject === project.id));
    });
    dialog.querySelector('.project-media-thumbnails').innerHTML = project.media.map((media, position) => `<button type="button" data-gallery-media="${position}" aria-label="View media: ${escapeHtml(media.title)}"><img src="${escapeHtml(media.poster || media.src)}" alt="" width="60" height="40"><span>${media.type === 'video' ? '▶ ' : ''}${escapeHtml(media.title)}</span></button>`).join('');
    dialog.querySelector('.project-modal-about').innerHTML = `<p>${escapeHtml(project.summary)}</p>
      <details><summary>How it works &amp; my contribution</summary><div><h3>My contribution</h3><p>${escapeHtml(project.proof)}</p><ol>${project.steps.map(step => `<li>${escapeHtml(step)}</li>`).join('')}</ol><p>${escapeHtml(project.decision)}</p><p class="project-modal-stack">${escapeHtml(project.stack.join(' · '))}</p>${project.url ? `<a href="${escapeHtml(project.url)}" target="_blank" rel="noopener">Visit ${escapeHtml(new URL(project.url).hostname)} ↗</a>` : ''}</div></details>`;
    dialog.querySelector('.project-modal-workspace').scrollTop = 0;
    show();
  }
  function show() {
    stopMedia();
    const media = project.media[index];
    dialog.querySelector('#project-media-project').textContent = project.title;
    dialog.querySelector('#project-media-title').textContent = media.title;
    dialog.querySelector('#project-media-caption').textContent = media.caption;
    dialog.querySelector('[data-media-position]').textContent = `${index + 1} / ${project.media.length}`;
    dialog.querySelectorAll('[data-gallery-media]').forEach(button => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.galleryMedia) === index));
    });
    dialog.querySelectorAll('[data-media-prev], [data-media-next]').forEach(button => { button.disabled = project.media.length === 1; });
    if (media.type === 'carousel') {
      const carousel = document.createElement('section');
      carousel.className = 'project-carousel';
      carousel.innerHTML = `<p class="project-carousel-hint">${media.items.length} cards · Swipe to browse · Tap a card to enlarge</p><div class="project-carousel-cards" tabindex="0" role="region" aria-label="Carousel cards">${media.items.map((card, cardIndex) => `<figure><a href="${escapeHtml(card.src)}" target="_blank" rel="noopener" aria-label="Enlarge carousel card ${cardIndex + 1} (opens in a new tab)"><img src="${escapeHtml(card.src)}" alt="${escapeHtml(card.caption)}" decoding="async"></a><figcaption>Card ${cardIndex + 1} of ${media.items.length}</figcaption></figure>`).join('')}</div>`;
      carousel.querySelectorAll('img').forEach(img => img.addEventListener('error', () => {
        img.closest('figure').querySelector('figcaption').textContent += ' — image unavailable';
      }, { once: true }));
      stage.replaceChildren(carousel);
      return;
    }
    const element = document.createElement(media.type === 'video' ? 'video' : 'img');
    element.src = media.src;
    if (media.type === 'video') {
      element.controls = true; element.playsInline = true; element.preload = 'metadata';
      element.poster = media.poster; element.setAttribute('aria-label', media.title);
    } else { element.alt = `${project.title} — ${media.title}`; }
    element.addEventListener('error', () => {
      const message = document.createElement('p');
      message.textContent = 'This media could not load. Close the viewer and try again.';
      stage.replaceChildren(message);
    }, { once: true });
    stage.replaceChildren(element);
  }
  function openProject(projectId, { mediaIndex = 0, returnFocus = document.activeElement } = {}) {
    const selected = PROJECTS.find(item => item.id === projectId);
    if (!selected?.media?.[mediaIndex]) return;
    trigger = returnFocus;
    selectProject(selected);
    if (mediaIndex !== 0) { index = mediaIndex; show(); }
    if (!dialog.open) {
      root.classList.add('project-media-open');
      dialog.showModal();
      onOpenChange(true);
    }
  }
  function handleMediaClick(event) {
    const link = event.target.closest('[data-project-media]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const selected = PROJECTS.find(item => item.id === link.dataset.projectMedia);
    const selectedIndex = Number(link.dataset.mediaIndex);
    if (!selected?.media?.[selectedIndex]) return;
    event.preventDefault();
    openProject(selected.id, { mediaIndex: selectedIndex, returnFocus: link });
  }
  root.addEventListener('click', handleMediaClick);
  function step(delta) { index = (index + delta + project.media.length) % project.media.length; show(); }
  dialog.querySelector('[data-media-close]').onclick = () => dialog.close();
  dialog.querySelector('[data-media-prev]').onclick = () => step(-1);
  dialog.querySelector('[data-media-next]').onclick = () => step(1);
  dialog.addEventListener('keydown', event => {
    // Keep the underlying profile and 3D keyboard shortcuts inactive.
    event.stopPropagation();
    if (event.target.tagName === 'VIDEO' || event.target.closest('.project-carousel')) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault(); step(event.key === 'ArrowLeft' ? -1 : 1);
    }
  });
  dialog.addEventListener('click', event => {
    const projectButton = event.target.closest('[data-gallery-project]');
    if (projectButton) {
      selectProject(PROJECTS.find(item => item.id === projectButton.dataset.galleryProject));
      return;
    }
    const mediaButton = event.target.closest('[data-gallery-media]');
    if (mediaButton) { index = Number(mediaButton.dataset.galleryMedia); show(); return; }
    if (event.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    stopMedia(); stage.replaceChildren();
    root.classList.remove('project-media-open');
    onOpenChange(false);
    if (trigger?.isConnected && !trigger.closest('[inert]')) trigger.focus();
    else root.querySelector('#view-profile')?.focus();
  });
  return { openProject, isOpen: () => dialog.open };
}
